<script setup lang="ts">
  import { computed } from 'vue';
  import {
    IconImage,
    DatePicker,
    FilterModal,
    ThemeProvider,
    NumberStepperInput,
    SelectInput,
  } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { FilteredListStatus, DocumentFormat } from '@/entities/Document';
  import type { FilterOptions } from '@/entities/Document';
  import { useDocumentFilterModal } from '@/features/Document';
  import { useFilterManagementModal } from '../model';
  import { useFilterStore } from '@/entities/Filter';

  const props = defineProps<{
    currentFilters?: FilterOptions;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: FilterOptions): void;
    (e: 'update:selectedDate', date: Date | null): void;
    (e: 'clear-filters'): void;
  }>();

  const filterStore = useFilterStore();

  const {
    formData,
    effectiveDate,
    validityDate,
    isEffectiveDateOpen,
    isValidityDateOpen,
    formatDate,
    applyFilters,
    clearFilters,
  } = useDocumentFilterModal(
    emit,
    undefined,
    (props.currentFilters || filterStore.filters) as Partial<FilterOptions>
  );

  const { physicalLocations, loadingLocations, locationsError } = useFilterManagementModal();

  const locationList = computed(() => {
    if (loadingLocations.value) {
      return [{ id: 0, name: 'Carregando...', disabled: true }];
    }

    if (locationsError.value) {
      return [{ id: 0, name: 'Erro ao carregar localizações', disabled: true }];
    }

    return physicalLocations.value;
  });

  const handleClearFilters = () => {
    clearFilters();
    emit('clear-filters');
  };
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @apply-filters="applyFilters"
    @clear-filters="handleClearFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="space-y-4">
          <div class="flex gap-4 p-6">
            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">
                Treinamento
              </h4>
              <div class="flex flex-col gap-2">
                <button
                  :class="[
                    'py-2 rounded-lg w-40',
                    formData.hasTraining === true
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  @click="formData.hasTraining = formData.hasTraining === true ? null : true"
                >
                  Com treinamento
                </button>
                <button
                  :class="[
                    'py-2 rounded-lg w-40',
                    formData.hasTraining === false
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  @click="formData.hasTraining = formData.hasTraining === false ? null : false"
                >
                  Sem treinamento
                </button>
              </div>
            </div>

            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">Distribuição</h4>
              <div class="flex flex-col items-start gap-2">
                <button
                  :class="[
                    'flex items-center justify-center py-2 px-4 rounded-lg w-40',
                    formData.isExternal === false
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  @click="formData.isExternal = formData.isExternal === false ? null : false"
                >
                  Interna
                </button>
                <button
                  :class="[
                    'flex items-center justify-center py-2 px-4 rounded-lg w-40',
                    formData.isExternal === true
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  @click="formData.isExternal = formData.isExternal === true ? null : true"
                >
                  Externa
                </button>
              </div>
            </div>
          </div>

          <div class="flex gap-4 p-6">
            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">Formato</h4>
              <div class="flex flex-col items-start gap-2">
                <button
                  :class="[
                    'flex items-center justify-center py-2 px-4 rounded-lg w-40',
                    formData.format === DocumentFormat.EL
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  @click="
                    formData.format = formData.format === DocumentFormat.EL ? '' : DocumentFormat.EL
                  "
                >
                  EL
                </button>
                <button
                  :class="[
                    'flex items-center justify-center py-2 px-4 rounded-lg w-40',
                    formData.format === DocumentFormat.EL_FS
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  @click="
                    formData.format =
                      formData.format === DocumentFormat.EL_FS ? '' : DocumentFormat.EL_FS
                  "
                >
                  EL/FS
                </button>
              </div>
            </div>

            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">Status</h4>
                <div class="grid grid-cols-2 gap-x-2 gap-y-2">
                <div class="flex flex-col items-start gap-2">
                  <button
                    :class="[
                      'flex items-center justify-center py-2 px-4 rounded-lg w-full min-w-0',
                      formData.status === FilteredListStatus.VIGENTE
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    @click="
                      formData.status =
                        formData.status === FilteredListStatus.VIGENTE
                          ? ''
                          : FilteredListStatus.VIGENTE
                    "
                  >
                    Vigente
                  </button>
                  <button
                    :class="[
                      'flex items-center justify-center py-2 px-4 rounded-lg w-full min-w-0',
                      formData.status === FilteredListStatus.INATIVO
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    @click="
                      formData.status =
                        formData.status === FilteredListStatus.INATIVO
                          ? ''
                          : FilteredListStatus.INATIVO
                    "
                  >
                    Inativo
                  </button>
                </div>
                <div class="flex flex-col items-start gap-2">
                  <button
                    :class="[
                      'flex items-center justify-center py-2 px-4 rounded-lg w-full min-w-0',
                      formData.status === FilteredListStatus.OBSOLETO
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    @click="
                      formData.status =
                        formData.status === FilteredListStatus.OBSOLETO
                          ? ''
                          : FilteredListStatus.OBSOLETO
                    "
                  >
                    Obsoleto
                  </button>
                  <button
                    :class="[
                      'flex items-center justify-center py-2 px-4 rounded-lg w-full min-w-0',
                      formData.status === FilteredListStatus.REVOGADO
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    @click="
                      formData.status =
                        formData.status === FilteredListStatus.REVOGADO
                          ? ''
                          : FilteredListStatus.REVOGADO
                    "
                  >
                    Revogado
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex py-12 px-4 gap-4 border-y border-gray-02">
            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">
                Data de Vigência
              </h4>
              <button
                class="flex items-center bg-white-01 px-8 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                @click="isEffectiveDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5"
                />
                <span class="grow text-center">{{
                  effectiveDate !== null ? formatDate(effectiveDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="effectiveDate"
                v-model:is-open="isEffectiveDateOpen"
                class="my-auto"
                @apply="isEffectiveDateOpen = false"
                @cancel="isEffectiveDateOpen = false"
              />
            </div>

            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">
                Data de Validade
              </h4>
              <button
                class="flex items-center bg-white-01 px-8 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                @click="isValidityDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5"
                />
                <span class="grow text-center">{{
                  validityDate !== null ? formatDate(validityDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="validityDate"
                v-model:is-open="isValidityDateOpen"
                class="my-auto"
                @apply="isValidityDateOpen = false"
                @cancel="isValidityDateOpen = false"
              />
            </div>
          </div>

          <div class="py-12 px-4">
            <div class="flex gap-4">
              <div class="w-1/4">
                <h4 class="font-medium text-gray-07 mb-2">
                  Revisões
                </h4>
                <NumberStepperInput
                  :model-value="formData.revision ?? null"
                  :min="0"
                  :step="1"
                  input-class="w-full pr-9"
                  wrapper-class="w-full"
                  @update:model-value="formData.revision = $event ?? undefined"
                />
              </div>

              <div class="w-3/4">
                <h4 class="font-medium text-gray-07 mb-2">
                  Local HTM
                </h4>
                <SelectInput
                  :model-value="formData.physicalLocation ?? ''"
                  :options="locationList"
                  :use-teleport="true"
                  label="Selecione um local"
                  max-height="12rem"
                  :disabled="loadingLocations"
                  @update:model-value="formData.physicalLocation = $event"
                />
                <div
                  v-if="locationsError"
                  class="text-red-500 text-sm mt-1"
                >
                  {{ locationsError }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
